import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../../context/UserContext';
import styles from "../modes.module.css";
import AITutor from '../AITutor/AITutor';
import BackButton from '../common/BackButton/BackButton';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL, apiRequest } from '../../config/api';
import Loader from '../common/Loader/Loader';

const AdaptiveMode = () => {
  const { userProgress, loading: userLoading, error: userError, updateProgress } = useUser();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [adaptiveStats, setAdaptiveStats] = useState(null);
  const initialized = useRef(false);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const { state } = useLocation();
  const difficulty = state?.difficulty || userProgress?.modeLevels?.adaptive || 1;
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  const [completingSession, setCompletingSession] = useState(false);
  const [loadingNextQuestion, setLoadingNextQuestion] = useState(false);
  const questionsGenerated = useRef(0);
  const [sessionId, setSessionId] = useState(null);
  const attemptedQuestions = useRef([]);

  const navigate = useNavigate();

  useEffect(() => {
    if (!userLoading && userProgress && !initialized.current) {
      startSession();
      initialized.current = true;
    }
  }, [userLoading, userProgress]);

  useEffect(() => {
    if (questions.length === 1) {
      setQuestionStartTime(Date.now());
    }
  }, [currentQuestion, questions]);

  const startSession = async () => {
    setLoading(true);
    try {
      // Start a new session for adaptive mode
      const sessionData = await apiRequest('/sessions/start', {
        method: 'POST',
        body: JSON.stringify({
          mode: 'adaptive',
          difficulty: difficulty,
          questions: [] // Start with empty questions, we'll add them as we go
        })
      });

      setSessionId(sessionData._id);
      localStorage.setItem('currentSessionId', sessionData._id);
      
      // Start generating questions
      await generateNextQuestion();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const generateNextQuestion = async () => {
    setLoadingNextQuestion(true);
    try {
      // Get topics from user progress
      const topics = userProgress?.topicsForAdaptiveLearning || ['Tenses', 'Subject-Verb Agreement', 'Articles'];
      
      // Determine which topic to use for this question
      // We'll cycle through topics to ensure balanced coverage
      const topicIndex = questionsGenerated.current % topics.length;
      const selectedTopic = topics[topicIndex];
      
      // Generate the question
      const params = new URLSearchParams({
        mode: 'adaptive',
        difficulty: difficulty,
        topic: selectedTopic,
        type: 'error_identification',
      });

      const response = await apiRequest(`/questions/generateOne?${params}`);
      
      // Add the new question to our questions array
      setQuestions(prevQuestions => [...prevQuestions, response]);
      questionsGenerated.current++;
      
      // If this is the first question, stop loading
      if (questionsGenerated.current === 1) {
        setLoading(false);
      }
      
      // Continue generating questions until we have enough questions
      // We want 3 questions per topic, but cap at 15 total questions
      const totalTopics = topics.length;
      const questionsPerTopic = 3;
      const maxQuestions = Math.min(totalTopics * questionsPerTopic, 15);
      
      if (questionsGenerated.current < maxQuestions) {
        // Generate next question in the background
        setTimeout(() => generateNextQuestion(), 500);
      }
    } catch (err) {
      console.error("Error generating question:", err);
      // Don't set error here to avoid interrupting the session
    } finally {
      setLoadingNextQuestion(false);
    }
  };

  const handleAnswer = async (answer) => {
    if (isSubmitting) return;
    
    setSelectedAnswer(answer);
    setSubmittingAnswer(true);
    setIsSubmitting(true);

    try {
      const currentQ = questions[currentQuestion];
      
      const timeSpent = (Date.now() - questionStartTime) / 1000;
      
      const submitResponse = await apiRequest(`/sessions/${sessionId}/individual-answer`, {
        method: 'POST',
        body: JSON.stringify({
          question: currentQ,
          answer,
          timeSpent
        })
      });

      setAnswers(prev => ({ ...prev, [currentQuestion]: answer }));
      attemptedQuestions.current.push({...currentQ, _id: submitResponse.id});
      setShowExplanation(true);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
      setSubmittingAnswer(false);
    }
  };

  const handleNext = () => {
    setShowExplanation(false);
    setSelectedAnswer(null);
    
    // Get the total number of questions we'll use in the session
    const topics = userProgress?.topicsForAdaptiveLearning || ['Tenses', 'Subject-Verb Agreement', 'Articles'];
    const questionsPerTopic = 3;
    const totalQuestions = Math.min(topics.length * questionsPerTopic, 15);

    // Check if this is the last generated question and we're still generating more
    const isLastGeneratedQuestion = currentQuestion === questions.length - 1;
    const stillGeneratingQuestions = questionsGenerated.current < 15;
    
    if (isLastGeneratedQuestion && stillGeneratingQuestions && loadingNextQuestion) {
      // Don't allow submission yet - we're still generating the next question
      return;
    }
    
    if (currentQuestion + 1 >= totalQuestions) {
      handleCompletion();
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handleCompletion = async () => {
    setCompletingSession(true);
    
    try {
      const endResponse = await apiRequest(`/sessions/${sessionId}/end`, {
        method: 'POST'
      });

      localStorage.removeItem('currentSessionId');
      updateProgress(endResponse);
      
      // Navigate to results page with session results and questions
      navigate('/results', { 
        state: { 
          sessionResults: endResponse,
          questions: attemptedQuestions.current
        } 
      });
    } catch (err) {
      setError(err.message);
      setCompletingSession(false);
    }
  };

  if (loading || userLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <svg className={styles.errorIcon} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
        <span>{error}</span>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / Math.min(
    (userProgress?.topicsForAdaptiveLearning?.length || 3) * 3, 
    15
  )) * 100;

  return (
    <div className={styles.container}>
      {completingSession && (
        <div className={styles.overlay}>
          <Loader size="large" text="Analyzing your results..." />
        </div>
      )}
      
      <div className={styles.card}>
        <AITutor 
          currentQuestion={questions[currentQuestion]}
          userAnswer={answers[currentQuestion]}
          isCorrect={answers[currentQuestion] === questions[currentQuestion]?.content.correctAnswer}
        />
        <div className={styles.header}>
          <h2 className={styles.titleAdaptive}>Adaptive Practice</h2>
          <span className={styles.questionCount}>
            {/* Calculate total questions based on topics */}
            Question {currentQuestion + 1} of {Math.min(
              (userProgress?.topicsForAdaptiveLearning?.length || 3) * 3, 
              15
            )}
          </span>
        </div>

        <div className={styles.progressContainer}>
          <div 
            className={styles.progressBar} 
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className={styles.questionTextAdaptive}>
          {currentQ?.content.text}
        </div>

        <div className={styles.optionsContainer}>
          {currentQ?.content.options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleAnswer(option)}
              className={`${styles.optionButton} ${styles.optionButtonAdaptive} ${
                showExplanation && option === currentQ.content.correctAnswer
                  ? styles.correct
                  : showExplanation && answers[currentQuestion] === option
                  ? styles.incorrect
                  : submittingAnswer && selectedAnswer === option
                  ? styles.selected
                  : ''
              }`}
              disabled={isSubmitting || showExplanation}
            >
              {submittingAnswer && selectedAnswer === option ? (
                <span className={styles.optionWithLoader}>
                  {option}
                  <span className={styles.miniLoader}></span>
                </span>
              ) : (
                option
              )}
            </button>
          ))}
        </div>

        {showExplanation && (
          <>
            <div className={styles.explanation}>
              {currentQ.content.explanation}
            </div>
            <button 
              className={styles.nextButton}
              onClick={handleNext}
              disabled={
                (currentQuestion === questions.length - 1 && 
                 questionsGenerated.current < userProgress?.topicsForAdaptiveLearning?.length * 3 && 
                 loadingNextQuestion)
              }
            >
              {currentQuestion + 1 >= userProgress?.topicsForAdaptiveLearning?.length * 3 ? 'Finish' : 'Next Question'}
            </button>
          </>
        )}

        {currentQ?.metadata?.topic && (
          <div className={styles.topicInfo}>
            <span>Topic:</span> {currentQ.metadata.topic}
          </div>
        )}

        <BackButton />
      </div>

      <div className={`${styles.footer} ${styles.footerAdaptive}`}>
        <p className={styles.footerText}>
          Questions are tailored to help you improve in your weak areas.
        </p>
      </div>
    </div>
  );
};

export default AdaptiveMode;